import { t } from 'i18next';
import { BackButton } from '../../../common/components/BackButton';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { AuthorizationError } from '../../../common/errors/errors';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { bind } from '../../../common/utils/decorators';
import { SuccessNotification } from '../../../common/utils/notifications/Notification';
import { JiraIssueCollector } from '../../../integrations/jira/models/JiraIssueCollector';
import { JiraRepository } from '../../../integrations/jira/services/JiraRepository';
import { IntegrationsRepository } from '../../../integrations/services/IntegrationsRepository';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { ModalManager } from '../../../modal-flows/services/ModalManager';
import { PATHS, TabNameLocalizationKeys } from '../../../routing/routes';
import { IssueCollectorCard } from '../components/IssueCollectorCard';
import { JiraAccordion } from '../components/JiraAccordion';

type Props = WithRouterProps & {};

type State = BaseState & {
  isLoading: boolean;
  jiraIssueCollector: JiraIssueCollector | null;
  currentIssueCollectorCode: string;
};

class Controller extends BaseController<Props, State> {
  private readonly integrationsRepository = new IntegrationsRepository();
  private readonly jiraRepository = new JiraRepository();
  private readonly modalManager = new ModalManager();

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.state = {
      ...super.getBaseState(),
      isLoading: true,
      jiraIssueCollector: null,
      currentIssueCollectorCode: '',
    };
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.jiraRepository.cancelAllRequests();
    this.integrationsRepository.cancelAllRequests();
  }

  protected shouldCheckPermissionToAccess(): boolean {
    return true;
  }

  protected async checkPermissionToAccess(): Promise<void> {
    const permission = await this.permissionsService.canUpdateJira();
    if (!permission) {
      throw new AuthorizationError(PATHS.ORGANIZATION_DASHBOARD);
    }

    this.fetchData();
  }

  async fetchData() {
    try {
      const integrations = await this.integrationsRepository.getIntegrations();
      const jiraIssueCollector = integrations.getJiraIssueCollector();

      this.setState({
        isLoading: false,
        jiraIssueCollector,
        currentIssueCollectorCode: jiraIssueCollector?.getIssueCollectorCode() ?? '',
      });
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async onIssueCollectorUnlinkClick() {
    const { confirmUnlink } = await this.modalManager.unlinkIntegration('integrationList.jiraIssueCollector.title', null);
    if (!confirmUnlink) {
      return;
    }
    try {
      this.setState({
        isLoading: true,
      });
      await this.jiraRepository.unlinkJiraIssueCollector();
      this.setState({
        isLoading: false,
        jiraIssueCollector: null,
        currentIssueCollectorCode: '',
      });
      this.showMessage(new SuccessNotification(t('jiraIssueCollector.updated')));
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  onIssueCollectorValueChange(value: string) {
    this.setState({
      currentIssueCollectorCode: value,
    });
  }

  @bind
  async onIssueCollectorSaveClick() {
    const { currentIssueCollectorCode } = this.state;

    try {
      this.setState({
        isLoading: true,
      });
      const updatedJiraIntegration = await this.jiraRepository.updateJiraIssueCollector(currentIssueCollectorCode);
      this.setState({
        isLoading: false,
        jiraIssueCollector: updatedJiraIntegration,
        currentIssueCollectorCode: updatedJiraIntegration?.getIssueCollectorCode() ?? '',
      });
      this.showMessage(new SuccessNotification(t('jiraIssueCollector.updated')));
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  render() {
    const { isLoading, jiraIssueCollector, currentIssueCollectorCode } = this.state;
    const isIssueCollectorEnabled = jiraIssueCollector !== null;
    const jiraIssueCollectorCode = jiraIssueCollector?.getIssueCollectorCode() ?? '';
    const isValueEqual = currentIssueCollectorCode === jiraIssueCollectorCode;

    return this.renderBase(
      <SecondaryNav title={t(TabNameLocalizationKeys.JIRA)} subtitle={t('jiraSubtitle')} getBreadcrumbs={this.getBreadcrumbs}>
        <BackButton sx={{ mb: 2 }} />
        <JiraAccordion
          isLoading={isLoading}
          isJiraEnabled={isIssueCollectorEnabled}
          isConfigForm={true}
          details={
            <IssueCollectorCard
              initialValue={jiraIssueCollector?.getIssueCollectorCode() ?? ''}
              value={currentIssueCollectorCode}
              onUnlinkClick={this.onIssueCollectorUnlinkClick}
              onValueChange={this.onIssueCollectorValueChange}
              onSaveClick={this.onIssueCollectorSaveClick}
              isSaveDisabled={isLoading || isValueEqual}
            />
          }
        />
      </SecondaryNav>
    );
  }

  @bind
  private getBreadcrumbs() {
    return this.breadcrumbFactory.getIntegrationBreadcrumbs({
      integrationName: t(TabNameLocalizationKeys.JIRA),
      integrationPath: PATHS.INTEGRATION_JIRA_FORM,
    });
  }
}

export const JiraIssueCollectorController = withRouter(Controller);
